/**
 * Shared JSON-LD emitter — replaces 28 ad-hoc <script> blocks across the
 * site. Accepts a single schema object or an array (for pages that emit
 * multiple schemas, e.g. Article + FAQPage + HowTo).
 *
 * XSS hardening: every emitted JSON payload has "<" replaced with its
 * unicode escape "\u003c" before being injected, per the Next.js 16
 * JSON-LD guide. This prevents a malicious string in dynamic schema
 * content from closing the <script> tag and injecting HTML.
 * Ref: https://nextjs.org/docs/app/guides/json-ld
 *
 * Usage:
 *   import JsonLd from "@/components/JsonLd";
 *   import { articleSchema, faqPageSchema } from "@/lib/schema";
 *   <JsonLd data={[articleSchema({...}), faqPageSchema([...])]} />
 */

type JsonLdData = Record<string, unknown> | Array<Record<string, unknown>>;

function serialize(schema: Record<string, unknown>): string {
  return JSON.stringify(schema).replace(/</g, "\\u003c");
}

export default function JsonLd({ data }: { data: JsonLdData }) {
  const payload = Array.isArray(data) ? data : [data];
  return (
    <>
      {payload.map((schema, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: serialize(schema) }}
        />
      ))}
    </>
  );
}
